import { useState, useEffect } from 'react';
import { Lead, LeadUpdateInput, Task, Opportunity, CustomerCompany } from '../types';
import { leadsService } from '../services/leadsService';
import { tasksService } from '../services/tasksService';
import { opportunitiesService } from '../services/opportunitiesService';
import { companiesService } from '../services/companiesService';
import { paymentScheduleService } from '../services/paymentScheduleService';
import { dataInitializer } from '../services/dataInitializer';
import { AuthUser } from '../services/authService';
import { toastService } from '../services/toastService';
import { getOrgId, getUserId, getUserName } from '../utils/userHelpers';

export const useFirebaseData = (
  user: AuthUser,
  onOpportunityWon?: (opportunity: Opportunity, customer: CustomerCompany) => void
) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataLoadTimeout, setDataLoadTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!user?.uid) return;

    // Set a timeout for data loading to prevent infinite loading
    const timeout = setTimeout(() => {
      console.warn('Data loading timeout reached, setting loading to false');
      setLoading(false);
      setError('Data loading timed out. You can still use the app with limited functionality.');
    }, 10000); // 10 second timeout
    
    setDataLoadTimeout(timeout);
    // Initialize sample data for new users with better error handling
    const initializeData = async () => {
      try {
        console.log('Starting data initialization for user:', user.uid);
        // Try to initialize data, but don't fail if permissions are missing
        try {
          await dataInitializer.initializeUserData(user.uid);
          console.log('✅ User data initialized successfully');
        } catch (permError: any) {
          console.warn('⚠️ Could not initialize Firebase data (using mock data):', permError.message);
          // Continue with mock data if Firebase isn't properly configured
        }
      } catch (error: any) {
        console.warn('⚠️ Firebase initialization failed (using mock data):', error.message);
        // Don't set error - just continue with mock data
      }
    };
    
    initializeData();

    const orgId = getOrgId(user);
    const userId = getUserId(user);
    const unsubscribeLeads = leadsService.onLeadsChange(userId, orgId, (leadsData) => {
      console.log('Leads data updated:', leadsData.length, 'leads');
      setLeads(leadsData);
      clearTimeout(timeout);
      setLoading(false);
    }, (error) => {
      console.error('Leads subscription error:', error);
      clearTimeout(timeout);
      setLoading(false);
      // Start with empty data instead of showing error
      setLeads([]);
    });

    const unsubscribeTasks = tasksService.onTasksChange(userId, orgId, (tasksData) => {
      console.log('Tasks data updated:', tasksData.length, 'tasks');
      setTasks(tasksData);
    }, (error) => {
      console.error('Tasks subscription error:', error);
      setTasks([]);
    }, user.profile?.role);

    const unsubscribeOpportunities = opportunitiesService.onOpportunitiesChange(userId, orgId, (opportunitiesData) => {
      console.log('Opportunities data updated:', opportunitiesData.length, 'opportunities');
      setOpportunities(opportunitiesData);
    }, (error) => {
      console.error('Opportunities subscription error:', error);
      setOpportunities([]);
    }, user.profile?.role);

    return () => {
      if (dataLoadTimeout) {
        clearTimeout(dataLoadTimeout);
      }
      unsubscribeLeads();
      unsubscribeTasks();
      unsubscribeOpportunities();
    };
  }, [user?.uid]);

  const createLead = async (leadData: Omit<Lead, 'id' | 'createdAt' | 'updatedAt' | 'notes' | 'activities' | 'emails'>) => {
    try {
      await leadsService.createLead({
        ...leadData,
        assignedTo: getUserId(user),
        orgId: getOrgId(user),
        createdBy: getUserId(user),
        createdByName: getUserName(user)
      });
      toastService.success('Lead created successfully!');
    } catch (error) {
      console.error('Error creating lead:', error);
      if (error.message?.includes('Missing or insufficient permissions')) {
        setError('Firebase Security Rules not configured. Please configure Firestore Security Rules in your Firebase Console to allow authenticated users to create leads.');
        console.error('🔥 Firebase Security Rules Error - Please configure in Firebase Console:');
        console.error('1. Go to Firebase Console → Firestore Database → Rules');
        console.error('2. Add this rule for leads collection:');
        console.error('   match /leads/{document} {');
        console.error('     allow create: if request.auth != null && request.resource.data.assignedTo == request.auth.uid;');
        console.error('     allow read, write: if request.auth != null && resource.data.assignedTo == request.auth.uid;');
        console.error('   }');
      } else {
        setError('Failed to create lead');
      }
    }
  };

  const updateLead = async (leadId: string, updates: LeadUpdateInput) => {
    try {
      await leadsService.updateLead(leadId, updates);
      toastService.success('Lead updated successfully!');
    } catch (error) {
      console.error('Error updating lead:', error);
      setError('Failed to update lead');
      toastService.error('Failed to update lead');
    }
  };

  const updateLeadStage = async (leadId: string, newStage: Lead['stage']) => {
    try {
      // Optimistic UI update
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, stage: newStage, updatedAt: new Date() } as Lead : l));
      await leadsService.updateLeadStage(leadId, newStage, getUserId(user));
      toastService.success(`Lead moved to ${newStage} stage`);
    } catch (error) {
      console.error('Error updating lead stage:', error);
      setError('Failed to update lead stage');
      toastService.error('Failed to update lead stage');
      // Revert by refetch (fallback: let subscription correct it on next tick)
    }
  };

  const deleteLead = async (leadId: string) => {
    try {
      setLeads(prev => prev.filter(l => l.id !== leadId));
      await leadsService.deleteLead(leadId);
      toastService.success('Kontakt gelöscht');
    } catch (error) {
      console.error('Error deleting lead:', error);
      setError('Failed to delete lead');
      toastService.error('Kontakt konnte nicht gelöscht werden');
      const orgId = getOrgId(user);
      if (user && orgId) {
        const updatedLeads = await leadsService.getLeads(user.uid, orgId);
        setLeads(updatedLeads);
      }
    }
  };

  const createTask = async (taskData: Omit<Task, 'id'>) => {
    try {
      const taskId = await tasksService.createTask({
        ...taskData,
        assignedTo: taskData.assignedTo !== undefined ? taskData.assignedTo : getUserId(user),
        orgId: getOrgId(user),
        createdBy: getUserId(user),
        createdByName: getUserName(user)
      });
      toastService.success('Task created successfully!');
      return taskId;
    } catch (error) {
      console.error('Error creating task:', error);
      setError('Failed to create task');
      toastService.error('Failed to create task');
      throw error;
    }
  };

  const toggleTask = async (taskId: string) => {
    try {
      await tasksService.toggleTaskCompletion(taskId);
      toastService.success('Task updated!');
    } catch (error) {
      console.error('Error toggling task:', error);
      setError('Failed to update task');
      toastService.error('Failed to update task');
    }
  };

  const assignTask = async (taskId: string, userId: string) => {
    try {
      await tasksService.assignTask(taskId, userId);
    } catch (error) {
      console.error('Error assigning task:', error);
      setError('Failed to assign task');
    }
  };

  const updateTaskFields = async (taskId: string, updates: Partial<Task>) => {
    try {
      await tasksService.updateTask(taskId, updates);
    } catch (error) {
      console.error('Error updating task fields:', error);
      setError('Failed to update task');
    }
  };

  const deleteTask = async (taskId: string) => {
    try {
      // Check permissions first
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;
      
      if (!tasksService.canDeleteTask(task, user.uid, user.profile?.role)) {
        setError('You do not have permission to delete this task');
        return;
      }

      // Optimistic update - remove from list immediately
      setTasks(prev => prev.filter(t => t.id !== taskId));
      
      // Delete from Firebase
      await tasksService.deleteTask(taskId);
    } catch (error) {
      console.error('Error deleting task:', error);
      setError('Failed to delete task');
      
      // Revert optimistic update on error
      const orgId = getOrgId(user);
      if (user && orgId) {
        const updatedTasks = await tasksService.getTasks(user.uid, orgId);
        setTasks(updatedTasks);
      }
    }
  };

  const createOpportunity = async (opportunityData: Omit<Opportunity, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      // Ensure we have the required user data
      const orgId = getOrgId(user);
      const userId = getUserId(user);
      const userName = getUserName(user);
      
      if (!orgId || !userId) {
        throw new Error('Missing user organization or user ID');
      }

      // Use the assignedTo from opportunityData if provided, otherwise use current user
      const finalOpportunityData = {
        ...opportunityData,
        assignedTo: opportunityData.assignedTo || userId,
        orgId: orgId,
        createdBy: userId,
        createdByName: userName
      };

      console.log('Creating opportunity with data:', finalOpportunityData);
      
      await opportunitiesService.createOpportunity(finalOpportunityData);
      toastService.success('Opportunity created successfully!');
    } catch (error) {
      console.error('Error creating opportunity:', error);
      const errorMessage = 'Failed to create opportunity: ' + (error instanceof Error ? error.message : 'Unknown error');
      setError(errorMessage);
      toastService.error('Failed to create opportunity');
      throw error; // Re-throw to let the modal handle it
    }
  };

  const updateOpportunityStage = async (opportunityId: string, newStage: Opportunity['stage']) => {
    try {
      const opportunity = opportunities.find(opp => opp.id === opportunityId);
      if (!opportunity) return;

      const updatedOpportunity = {
        ...opportunity,
        stage: newStage,
        probability: getStageProbability(newStage),
        actualCloseDate: newStage === 'won' ? new Date() : opportunity.actualCloseDate,
        activities: [
          ...opportunity.activities,
          {
            id: Date.now().toString(),
            type: 'stage_changed' as const,
            description: `Opportunity moved to ${newStage} stage`,
            createdAt: new Date(),
            createdBy: user.displayName || user.email || 'User'
          }
        ]
      };

      // Optimistic UI update
      setOpportunities(prev => prev.map(o => o.id === opportunityId ? updatedOpportunity : o));
      await opportunitiesService.updateOpportunity(opportunityId, updatedOpportunity);

      // When opportunity is won, prepare for payment schedule creation
      if (newStage === 'won' && opportunity.companyId && opportunity.value && opportunity.value > 0) {
        try {
          // Fetch the company to check if it's a customer
          const company = await companiesService.getCompanyById(opportunity.companyId);
          
          if (company) {
            // Convert to customer if not already
            if (!company.isCustomer) {
              await companiesService.updateCompany(opportunity.companyId, {
                isCustomer: true,
                customerSince: new Date()
              });
              console.log(`Converted company ${company.name} to customer`);
            }

            // Trigger payment schedule editor modal
            const customerCompany = company as CustomerCompany;
            if (onOpportunityWon) {
              onOpportunityWon(updatedOpportunity, customerCompany);
            } else {
              toastService.info('Bitte Zahlungsplan für gewonnene Opportunity erstellen');
            }
          }
        } catch (error) {
          console.error('Error preparing payment schedule:', error);
          toastService.warning('Opportunity gewonnen. Bitte Zahlungsplan manuell erstellen.');
        }
      }
    } catch (error) {
      console.error('Error updating opportunity stage:', error);
      setError('Failed to update opportunity stage');
    }
  };

  const addLeadToOpportunity = async (opportunityId: string, leadId: string) => {
    try {
      // Find the lead to add
      const leadToAdd = leads.find(l => l.id === leadId);
      if (!leadToAdd) {
        console.error('Lead not found:', leadId);
        return;
      }

      // Optimistic update - add lead to opportunity immediately
      setOpportunities(prev => prev.map(opp => {
        if (opp.id !== opportunityId) return opp;

        // Check if lead is already in the opportunity
        if ((opp.leadIds || []).includes(leadId)) {
          console.log('Lead already in opportunity');
          return opp;
        }

        // Add lead to the opportunity
        return {
          ...opp,
          leadIds: [...(opp.leadIds || []), leadId],
          leads: [...(opp.leads || []), leadToAdd],
          summary: {
            ...(opp.summary || {}),
            totalLeads: (opp.summary?.totalLeads || 0) + 1,
            lastActivity: new Date()
          }
        };
      }));

      // Then update in Firebase
      await opportunitiesService.addLeadToOpportunity(opportunityId, leadId);
      console.log('Successfully added lead to opportunity:', { opportunityId, leadId });
    } catch (error) {
      console.error('Error linking lead to opportunity:', error);
      setError('Failed to add lead to opportunity');
      
      // Revert optimistic update on error by re-fetching opportunities
      const orgId = getOrgId(user);
      if (user && orgId) {
        const updatedOpportunities = await opportunitiesService.getOpportunities(user.uid, orgId);
        setOpportunities(updatedOpportunities);
      }
    }
  };

  const updateOpportunity = async (opportunityId: string, updates: Partial<Opportunity>) => {
    try {
      // Optimistic UI update
      setOpportunities(prev => prev.map(o => {
        if (o.id !== opportunityId) return o;
        const patched: Opportunity = {
          ...o,
          ...updates,
          expectedCloseDate: updates.expectedCloseDate ? new Date(updates.expectedCloseDate as any) : o.expectedCloseDate,
          actualCloseDate: updates.actualCloseDate ? new Date(updates.actualCloseDate as any) : o.actualCloseDate,
          nextFollowUp: updates.nextFollowUp ? new Date(updates.nextFollowUp as any) : o.nextFollowUp,
          company: updates.company ? { ...o.company, ...updates.company } as any : o.company,
        } as Opportunity;
        return patched;
      }));
      await opportunitiesService.updateOpportunity(opportunityId, updates);
    } catch (error) {
      console.error('Error updating opportunity:', error);
      setError('Failed to update opportunity');
    }
  };

  const getStageProbability = (stage: Opportunity['stage']): number => {
    const probabilities: Record<Opportunity['stage'], number> = {
      prospecting: 20,
      qualification: 40,
      proposal: 60,
      negotiation: 75,
      closing: 90,
      won: 100,
      lost: 0
    };
    return probabilities[stage];
  };

  return {
    leads,
    tasks,
    opportunities,
    loading,
    error,
    createLead,
    updateLead,
    updateLeadStage,
    deleteLead,
    createTask,
    toggleTask,
    assignTask,
    updateTaskFields,
    deleteTask,
    createOpportunity,
    updateOpportunityStage,
    updateOpportunity,
    addLeadToOpportunity
  };
};
